import type { TextRenderSnapshot } from '@/framework/services/text-render';
import { TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { DEFAULT_LINE_HEIGHT_FACTOR, USE_ENGINE_KEY } from '../constants';

/**
 * 旧 DOM 排版遗留文字框的兼容边界。
 *
 * Q: 为什么需要这个模块？
 * A: 编辑器改版前的文字图层由 DOM 测量排版，持久化的文字框行内尺寸是浏览器量出来的。新引擎的行宽与
 *    浏览器存在亚像素到几像素的差异，而文字框行内尺寸同时是引擎软换行的唯一依据，因此原本单行的旧
 *    文字会被重新断行 —— 单行变两行，图层高度随之翻倍。
 *
 * 兼容动作全部集中在本模块，只有两个出口：
 * 1. {@link shouldKeepLegacyFrameUnwrapped} 让渲染期对未标记的旧单行文字关闭软换行，保证画面不掉行；
 * 2. {@link calibrateLegacyTextFrame} 用引擎排版事实校准行内尺寸并写入标记。
 *
 * 校准由 Text Layer 宿主在首次拿到 ready 排版时调用一次，因此兼容对下游不可见：用户能选中、编辑或
 * 保存图层时，它的行内尺寸已经是引擎产出的，编辑、resize、保存和新建路径都不需要感知本模块。新建
 * 图层在创建入口声明 useEngine，直接走正常换行，不进入旧文字校准。
 *
 * Q: 为什么标记必须持久化，不能用 `__` 运行时字段？
 * A: 出身是数据的属性而不是会话状态。持久化还带来一个必要的正确性：复制粘贴旧图层时 `extraInfo`
 *    会跟着副本走，副本仍被识别为旧数据；运行时字段在拷贝和 server→client 重建中会丢失，副本会被
 *    误判成新数据而立刻掉行。
 */

/**
 * 根据统一字号和块轴尺寸判断图层是否可能是旧单行文字。
 *
 * 混合字号的各行高度可能不同，不能用最大字号推断行数，因此直接排除。
 * 非空分段字号一致时，沿用 1.5 倍单行高度的兼容阈值；没有有效分段时使用图层字号。
 *
 * @param layer - 当前文字图层。
 * @returns 块轴尺寸只够单行时返回 true。
 */
const hasSingleLineBlockSize = (layer: ILayerProps): boolean => {
    const data = layer.data as ITextLayerProps;
    const contents = (data.textContents ?? []).filter((content) => content.text.length > 0);
    const fontSize = contents[0]?.fontSize ?? data.fontSize;
    if (contents.some((content) => content.fontSize !== fontSize)) return false;

    const singleLineSize = (data.lineSpacing || DEFAULT_LINE_HEIGHT_FACTOR) * fontSize;
    const blockSize = data.writingMode === TextWritingType.HORIZONTAL_TB ? layer.height : layer.width;

    return blockSize > 0 && blockSize < singleLineSize * 1.5;
};

/**
 * 判断渲染期是否应当为该图层关闭软换行。
 *
 * 仅覆盖「行内尺寸来自旧 DOM 排版、且原本就是单行」的普通文字：没有引擎排版标记，文本内没有换行符，
 * 有效文本字号一致，块轴尺寸只够一行。变形文字走独立渲染路径，不参与该兼容。
 *
 * 标记之外仍然需要单行判断，因为标记区分不了旧单行与旧段落，而旧段落必须继续软换行。
 *
 * 编辑中且文案已被改写时不再保护：此时文字框的 DOM 测量结果已经不描述当前文本，继续关闭软换行会让
 * 输入一直向行内轴生长而不折行。正常情况下图层在可编辑之前就已经完成校准并带上标记，该条件只在
 * 「首轮 ready 排版之前就进入编辑」的窄窗口生效，把行为兜回按文字框换行。
 *
 * 命中后行宽差异只会表现为几像素的视觉溢出，不会改变行数；Layout Box 仍使用图层声明的行内尺寸，
 * 因此对齐基准、文字背景和图层位置都保持旧数据原样。
 *
 * @param layer - 当前文字图层或编辑期工作值。
 * @returns 需要关闭软换行时返回 true。
 */
export const shouldKeepLegacyFrameUnwrapped = (layer: ILayerProps): boolean => {
    if (layer.extraInfo?.[USE_ENGINE_KEY] === true) return false;
    if (layer.__isOnTextEditing && layer.extraInfo?.modified === true) return false;

    const data = layer.data as ITextLayerProps;
    if (data.shapeExtension) return false;
    if (!data.text || data.text.includes('\n')) return false;

    return hasSingleLineBlockSize(layer);
};

/**
 * 用引擎排版事实校准旧文字框的行内尺寸，并写入引擎排版标记。
 *
 * 只放宽、不收窄：仅当引擎排版出的行内尺寸大于旧尺寸时才改写，因此旧数据的对齐基准最多偏移溢出量的
 * 一半，不会因为文字框收缩而整体移位。入参 snapshot 必须来自当前图层自身的 ready 排版；命中旧单行
 * 兼容时软换行已关闭，因此内容尺寸就是「不换行需要多大」。其他普通文字只补标记，不改尺寸。
 *
 * 编辑中的工作值不回写；已标记、变形文字或无有效内容尺寸时返回原图层引用。
 *
 * @param layer - 当前文字图层。
 * @param snapshot - 当前图层的排版快照。
 * @returns 必要时校准尺寸并标记后的新图层；无需回写时返回原图层。
 */
export const calibrateLegacyTextFrame = (layer: ILayerProps, snapshot: TextRenderSnapshot): ILayerProps => {
    if (layer.extraInfo?.[USE_ENGINE_KEY] === true || layer.__isOnTextEditing) return layer;
    if ((layer.data as ITextLayerProps).shapeExtension) return layer;

    const isHorizontal = snapshot.writingMode === 'horizontal-tb';
    const contentInlineSize =
        (isHorizontal ? snapshot.contentWidth : snapshot.contentHeight) + snapshot.listMarkerIndent;
    if (!(contentInlineSize > 0)) return layer;

    const nextLayer: ILayerProps = { ...layer };
    if (shouldKeepLegacyFrameUnwrapped(layer)) {
        if (isHorizontal) {
            nextLayer.width = Math.max(layer.width, contentInlineSize);
        } else {
            nextLayer.height = Math.max(layer.height, contentInlineSize);
        }
    }
    nextLayer.extraInfo = { ...layer.extraInfo, [USE_ENGINE_KEY]: true };

    return nextLayer;
};
